import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";

import {
  fetchAdminSquads,
  fetchAdminChallenges,
  createMatch,
  createTraining,
  createTeam,
  deleteAdminChallenge,
  deleteAdminSquad,
  deleteMatch,
  deleteTraining,
  deleteTeam,
  fetchMembersPage,
  fetchMatches,
  fetchTeamJoinRequests,
  fetchTrainings,
  fetchTeams,
  reviewTeamJoinRequest,
  reviewAdminSquad,
  updateAdminChallengeStatus,
  updateTraining,
  updateMatch,
  updateTeam
} from "@/features/admin/api";
import { useAdminSession } from "@/features/admin/useAdminSession";
import type { AdminChallenge, AdminMatch, AdminMember, AdminPlayer, AdminSquad, AdminTeam, AdminTeamJoinRequest, AdminTraining } from "@/features/admin/types";

import PaginationControls from "./PaginationControls";

const SPORTS = ["Football", "Basketball", "Volleyball", "Tennis de Table", "Natation", "Athlétisme"];
const COULEURS = ["#E8521A", "#1A8C7A", "#1A1A1A", "#F59E0B", "#8B5CF6", "#EF4444", "#3B82F6", "#10B981"];
const TRAINING_DAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const typeRencontreConfig: Record<AdminMatch["type"], { label: string; cls: string }> = {
  amical: { label: "Amical", cls: "bg-gray-100 text-gray-600" },
  championnat: { label: "Championnat", cls: "bg-orange-100 text-[#E8521A]" },
  coupe: { label: "Coupe", cls: "bg-amber-100 text-amber-600" },
  tournoi: { label: "Tournoi", cls: "bg-teal-100 text-[#1A8C7A]" }
};

const statutRencontreConfig: Record<AdminMatch["statut"], { label: string; cls: string }> = {
  programme: { label: "Programmé", cls: "bg-blue-100 text-blue-600" },
  en_cours: { label: "En cours", cls: "bg-emerald-100 text-emerald-600" },
  termine: { label: "Terminé", cls: "bg-gray-100 text-gray-500" },
  annule: { label: "Annulé", cls: "bg-red-100 text-red-500" }
};

const statutSquadConfig: Record<AdminSquad["status"], { label: string; cls: string }> = {
  ouvert: { label: "Ouvert", cls: "bg-emerald-100 text-emerald-600" },
  complet: { label: "Complet", cls: "bg-orange-100 text-[#E8521A]" },
  ferme: { label: "Fermé", cls: "bg-gray-100 text-gray-500" }
};

const validationSquadConfig: Record<AdminSquad["approvalStatus"], { label: string; cls: string }> = {
  en_attente: { label: "En attente", cls: "bg-amber-100 text-amber-700" },
  approuve: { label: "Approuvé", cls: "bg-emerald-100 text-emerald-600" },
  refuse: { label: "Refusé", cls: "bg-red-100 text-red-500" }
};

const SQUAD_ACCENTS: Record<string, string> = {
  "bg-orange-500": "#f97316",
  "bg-teal-600": "#0d9488",
  "bg-purple-500": "#a855f7",
  "bg-green-600": "#16a34a",
  "bg-blue-500": "#3b82f6",
  "bg-cyan-500": "#06b6d4",
  "bg-amber-500": "#f59e0b",
  "bg-pink-500": "#ec4899",
  "bg-yellow-500": "#eab308",
  "bg-gray-700": "#374151"
};

function getSquadAccent(color: string) {
  return SQUAD_ACCENTS[color] ?? SQUAD_ACCENTS["bg-gray-700"];
}

type ActiveSection = "equipes" | "demandes_equipes" | "squads" | "rencontres" | "entrainements" | "defis";
type DeleteIntent =
  | {
      kind: "team";
      id: string;
      name: string;
      message: string;
    }
  | {
      kind: "squad";
      id: string;
      name: string;
      message: string;
    }
  | {
      kind: "match";
      id: string;
      name: string;
      message: string;
    }
  | {
      kind: "training";
      id: string;
      name: string;
      message: string;
    }
  | {
      kind: "challenge";
      id: string;
      name: string;
      message: string;
    };

function makeTempId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

const TEAM_MEMBER_PAGE_SIZE = 8;
const TEAMS_PER_PAGE = 2;
const SQUADS_PER_PAGE = 2;
const MATCHES_PER_PAGE = 3;
const TRAININGS_PER_PAGE = 3;
const CHALLENGES_PER_PAGE = 3;
const TRAINING_MIGRATION_MESSAGE =
  "La base locale n'a pas encore reçu la migration des entraînements. Lancez `npm run prisma:migrate:dev`, puis redémarrez le backend.";

function isMissingTrainingTableError(message: string) {
  return message.includes("TeamTraining");
}

export default function EquipesTab() {
  const { request } = useAdminSession();
  const [section, setSection] = useState<ActiveSection>("equipes");
  const autoOpenedPendingSquadsRef = useRef(false);
  const [equipes, setEquipes] = useState<AdminTeam[]>([]);
  const [teamJoinRequests, setTeamJoinRequests] = useState<AdminTeamJoinRequest[]>([]);
  const [squads, setSquads] = useState<AdminSquad[]>([]);
  const [rencontres, setRencontres] = useState<AdminMatch[]>([]);
  const [trainings, setTrainings] = useState<AdminTraining[]>([]);
  const [challenges, setChallenges] = useState<AdminChallenge[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [trainingNotice, setTrainingNotice] = useState<string | null>(null);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingTeamJoinRequests, setLoadingTeamJoinRequests] = useState(true);
  const [loadingSquads, setLoadingSquads] = useState(true);
  const [loadingMatches, setLoadingMatches] = useState(true);
  const [loadingTrainings, setLoadingTrainings] = useState(true);
  const [loadingChallenges, setLoadingChallenges] = useState(true);
  const [deletingItemKey, setDeletingItemKey] = useState<string | null>(null);
  const [reviewingTeamJoinRequestId, setReviewingTeamJoinRequestId] = useState<string | null>(null);
  const [deleteIntent, setDeleteIntent] = useState<DeleteIntent | null>(null);
  const [membersCatalog, setMembersCatalog] = useState<AdminMember[]>([]);
  const [membersCatalogPage, setMembersCatalogPage] = useState(1);
  const [membersCatalogTotalPages, setMembersCatalogTotalPages] = useState(1);
  const [membersCatalogTotal, setMembersCatalogTotal] = useState(0);
  const [membersCatalogSearch, setMembersCatalogSearch] = useState("");
  const [loadingMembersCatalog, setLoadingMembersCatalog] = useState(false);
  const deferredMembersCatalogSearch = useDeferredValue(membersCatalogSearch.trim());
  const [teamsPage, setTeamsPage] = useState(1);
  const [squadsPage, setSquadsPage] = useState(1);
  const [matchesPage, setMatchesPage] = useState(1);
  const [trainingsPage, setTrainingsPage] = useState(1);
  const [challengesPage, setChallengesPage] = useState(1);

  const [showEquipeForm, setShowEquipeForm] = useState(false);
  const [editEquipe, setEditEquipe] = useState<AdminTeam | null>(null);
  const [equipeForm, setEquipeForm] = useState({
    nom: "",
    sport: "Football",
    couleur: "#E8521A",
    entraineur: "",
    statut: "actif" as AdminTeam["statut"]
  });
  const [joueurs, setJoueurs] = useState<AdminPlayer[]>([]);
  const [selectedTeamMember, setSelectedTeamMember] = useState<AdminMember | null>(null);
  const [joueurForm, setJoueurForm] = useState({ poste: "", numero: "" });
  const [selectedEquipe, setSelectedEquipe] = useState<AdminTeam | null>(null);

  const [showRencontreForm, setShowRencontreForm] = useState(false);
  const [editRencontre, setEditRencontre] = useState<AdminMatch | null>(null);
  const [rencontreForm, setRencontreForm] = useState({
    equipeD: "",
    equipeE: "",
    sport: "",
    date: "",
    heure: "",
    lieu: "",
    type: "amical" as AdminMatch["type"],
    statut: "programme" as AdminMatch["statut"],
    scoreD: "",
    scoreE: ""
  });
  const [showTrainingForm, setShowTrainingForm] = useState(false);
  const [editTraining, setEditTraining] = useState<AdminTraining | null>(null);
  const [trainingForm, setTrainingForm] = useState({
    equipeId: "",
    jour: TRAINING_DAYS[0] ?? "Lundi",
    heure: "",
    lieu: ""
  });

  const loadTeams = useCallback(async () => {
    setLoadingTeams(true);
    setError(null);

    try {
      const nextTeams = await fetchTeams(request);
      setEquipes(nextTeams);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les équipes";
      setError(message);
    } finally {
      setLoadingTeams(false);
    }
  }, [request]);

  const loadTeamJoinRequests = useCallback(async () => {
    setLoadingTeamJoinRequests(true);
    setError(null);

    try {
      const nextRequests = await fetchTeamJoinRequests(request);
      setTeamJoinRequests(nextRequests);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les demandes d'équipe";
      setError(message);
    } finally {
      setLoadingTeamJoinRequests(false);
    }
  }, [request]);

  const loadMatches = useCallback(async () => {
    setLoadingMatches(true);
    setError(null);

    try {
      const nextMatches = await fetchMatches(request);
      setRencontres(nextMatches);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les rencontres";
      setError(message);
    } finally {
      setLoadingMatches(false);
    }
  }, [request]);

  const loadTrainings = useCallback(async () => {
    setLoadingTrainings(true);
    setTrainingNotice(null);

    try {
      const nextTrainings = await fetchTrainings(request);
      setTrainings(nextTrainings);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les entraînements";
      if (isMissingTrainingTableError(message)) {
        setTrainings([]);
        setTrainingNotice(TRAINING_MIGRATION_MESSAGE);
      } else {
        setError(message);
      }
    } finally {
      setLoadingTrainings(false);
    }
  }, [request]);

  const loadSquads = useCallback(async () => {
    setLoadingSquads(true);
    setError(null);

    try {
      const nextSquads = await fetchAdminSquads(request);
      setSquads(nextSquads);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les squads";
      setError(message);
    } finally {
      setLoadingSquads(false);
    }
  }, [request]);

  const loadChallenges = useCallback(async () => {
    setLoadingChallenges(true);
    setError(null);

    try {
      const nextChallenges = await fetchAdminChallenges(request);
      setChallenges(nextChallenges);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Impossible de charger les défis";
      setError(message);
    } finally {
      setLoadingChallenges(false);
    }
  }, [request]);

  const loadMembersCatalog = useCallback(async () => {
    setLoadingMembersCatalog(true);

    try {
      const page = await fetchMembersPage(request, {
        page: membersCatalogPage,
        limit: TEAM_MEMBER_PAGE_SIZE,
        search: deferredMembersCatalogSearch,
        status: "actif"
      });
      setMembersCatalog(page.items);
      setMembersCatalogPage(page.page);
      setMembersCatalogTotal(page.total);
      setMembersCatalogTotalPages(Math.max(page.totalPages, 1));
    } catch {
      setMembersCatalog([]);
      setMembersCatalogTotal(0);
      setMembersCatalogTotalPages(1);
    } finally {
      setLoadingMembersCatalog(false);
    }
  }, [deferredMembersCatalogSearch, membersCatalogPage, request]);

  useEffect(() => {
    void loadTeams();
    void loadTeamJoinRequests();
    void loadSquads();
    void loadMatches();
    void loadTrainings();
    void loadChallenges();
  }, [loadChallenges, loadMatches, loadSquads, loadTeamJoinRequests, loadTeams, loadTrainings]);

  useEffect(() => {
    if (!showEquipeForm) {
      return;
    }

    void loadMembersCatalog();
  }, [loadMembersCatalog, showEquipeForm]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 3000);
  };

  const requestDeleteEquipe = (team: AdminTeam) => {
    setDeleteIntent({
      kind: "team",
      id: team.id,
      name: team.nom,
      message: "Supprimer cette équipe et son espace privé de discussion ?"
    });
  };

  const requestDeleteSquad = (squad: AdminSquad) => {
    setDeleteIntent({
      kind: "squad",
      id: squad.id,
      name: squad.name,
      message: "Supprimer ce squad, son chat privé et les liaisons membres associées ?"
    });
  };

  const requestDeleteMatch = (match: AdminMatch) => {
    setDeleteIntent({
      kind: "match",
      id: match.id,
      name: `${match.equipeDomicile} vs ${match.equipeExterieur}`,
      message: "Supprimer définitivement cette rencontre du calendrier ?"
    });
  };

  const requestDeleteTraining = (training: AdminTraining) => {
    setDeleteIntent({
      kind: "training",
      id: training.id,
      name: `${training.equipeNom} · ${training.jour} ${training.heure}`,
      message: "Supprimer définitivement cet entraînement du planning ?"
    });
  };

  const requestDeleteChallenge = (challenge: AdminChallenge) => {
    setDeleteIntent({
      kind: "challenge",
      id: challenge.id,
      name: challenge.title,
      message: "Supprimer définitivement ce défi et toutes ses participations ?"
    });
  };

  const openNewEquipe = () => {
    setEditEquipe(null);
    setEquipeForm({ nom: "", sport: "Football", couleur: "#E8521A", entraineur: "", statut: "actif" });
    setJoueurs([]);
    setSelectedTeamMember(null);
    setMembersCatalogSearch("");
    setMembersCatalogPage(1);
    setJoueurForm({ poste: "", numero: "" });
    setShowEquipeForm(true);
  };

  const openEditEquipe = (team: AdminTeam) => {
    setEditEquipe(team);
    setEquipeForm({
      nom: team.nom,
      sport: team.sport,
      couleur: team.couleur,
      entraineur: team.entraineur,
      statut: team.statut
    });
    setJoueurs(team.joueurs);
    setSelectedTeamMember(null);
    setMembersCatalogSearch("");
    setMembersCatalogPage(1);
    setJoueurForm({ poste: "", numero: "" });
    setShowEquipeForm(true);
  };

  const addJoueur = () => {
    if (!selectedTeamMember || !joueurForm.poste.trim()) {
      setError("Sélectionne un membre actif et renseigne son poste.");
      return;
    }

    if (joueurs.some((player) => player.userId === selectedTeamMember.id)) {
      setError("Ce membre est déjà lié à un joueur de cette équipe.");
      return;
    }

    setJoueurs((previous) => [
      ...previous,
      {
        id: makeTempId(),
        nom: `${selectedTeamMember.prenom} ${selectedTeamMember.nom}`,
        poste: joueurForm.poste.trim(),
        numero: Number(joueurForm.numero) || previous.length + 1,
        userId: selectedTeamMember.id,
        membreNom: `${selectedTeamMember.prenom} ${selectedTeamMember.nom}`,
        membreEmail: selectedTeamMember.email
      }
    ]);
    setSelectedTeamMember(null);
    setJoueurForm({ poste: "", numero: "" });
  };

  const removeJoueur = (id: string) => {
    setJoueurs((previous) => previous.filter((player) => player.id !== id));
  };

  const saveEquipe = async () => {
    if (!equipeForm.nom.trim() || equipeForm.entraineur.trim().length < 2) {
      setError("Le nom de l'équipe et celui de l'entraîneur sont obligatoires.");
      return;
    }

    if (joueurs.some((joueur) => !joueur.userId)) {
      setError("Chaque joueur de l'équipe doit être relié à un compte membre actif.");
      return;
    }

    const payload = {
      nom: equipeForm.nom.trim(),
      sport: equipeForm.sport,
      couleur: equipeForm.couleur,
      entraineur: equipeForm.entraineur.trim(),
      statut: equipeForm.statut,
      joueurs
    };

    try {
      if (editEquipe) {
        await updateTeam(request, editEquipe.id, payload);
        showToast("Équipe mise à jour");
      } else {
        await createTeam(request, payload);
        showToast("Équipe créée avec succès");
      }

      setShowEquipeForm(false);
      setSelectedEquipe(null);
      await loadTeams();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement impossible";
      setError(message);
    }
  };

  const handleDeleteEquipe = async (id: string) => {
    setDeletingItemKey(`team:${id}`);
    try {
      await deleteTeam(request, id);
      setSelectedEquipe((current) => (current?.id === id ? null : current));
      setDeleteIntent(null);
      showToast("Équipe supprimée");
      await loadTeams();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    } finally {
      setDeletingItemKey(null);
    }
  };

  const handleDeleteSquad = async (id: string) => {
    setDeletingItemKey(`squad:${id}`);
    try {
      await deleteAdminSquad(request, id);
      setDeleteIntent(null);
      showToast("Squad supprimé");
      await loadSquads();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    } finally {
      setDeletingItemKey(null);
    }
  };

  const openNewRencontre = () => {
    const firstMatchableSport = matchReadySports[0];

    if (!firstMatchableSport) {
      setError("Crée d'abord au moins deux équipes réelles du même sport pour planifier une rencontre.");
      return;
    }

    const firstHomeTeam = equipes.find((team) => team.sport === firstMatchableSport);
    const firstAwayTeam = equipes.find((team) => team.sport === firstMatchableSport && team.id !== firstHomeTeam?.id);

    setEditRencontre(null);
    setRencontreForm({
      equipeD: firstHomeTeam?.nom ?? "",
      equipeE: firstAwayTeam?.nom ?? "",
      sport: firstMatchableSport,
      date: "",
      heure: "",
      lieu: "",
      type: "amical",
      statut: "programme",
      scoreD: "",
      scoreE: ""
    });
    setShowRencontreForm(true);
  };

  const openNewTraining = () => {
    const firstTeam = equipes[0];

    if (!firstTeam) {
      setError("Crée d'abord une vraie équipe avant de programmer un entraînement.");
      return;
    }

    setEditTraining(null);
    setTrainingForm({
      equipeId: firstTeam.id,
      jour: TRAINING_DAYS[0] ?? "Lundi",
      heure: "",
      lieu: ""
    });
    setShowTrainingForm(true);
  };

  const openEditRencontre = (match: AdminMatch) => {
    setEditRencontre(match);
    setRencontreForm({
      equipeD: match.equipeDomicile,
      equipeE: match.equipeExterieur,
      sport: match.sport,
      date: match.date,
      heure: match.heure,
      lieu: match.lieu,
      type: match.type,
      statut: match.statut,
      scoreD: match.scoreDomicile !== null && match.scoreDomicile !== undefined ? String(match.scoreDomicile) : "",
      scoreE: match.scoreExterieur !== null && match.scoreExterieur !== undefined ? String(match.scoreExterieur) : ""
    });
    setShowRencontreForm(true);
  };

  const openResultRencontre = (match: AdminMatch) => {
    setEditRencontre(match);
    setRencontreForm({
      equipeD: match.equipeDomicile,
      equipeE: match.equipeExterieur,
      sport: match.sport,
      date: match.date,
      heure: match.heure,
      lieu: match.lieu,
      type: match.type,
      statut: "termine",
      scoreD: match.scoreDomicile !== null && match.scoreDomicile !== undefined ? String(match.scoreDomicile) : "0",
      scoreE: match.scoreExterieur !== null && match.scoreExterieur !== undefined ? String(match.scoreExterieur) : "0"
    });
    setShowRencontreForm(true);
  };

  const openEditTraining = (training: AdminTraining) => {
    setEditTraining(training);
    setTrainingForm({
      equipeId: training.equipeId,
      jour: training.jour,
      heure: training.heure,
      lieu: training.lieu
    });
    setShowTrainingForm(true);
  };

  const saveRencontre = async () => {
    if (!rencontreForm.equipeD.trim() || !rencontreForm.equipeE.trim() || !rencontreForm.date || !rencontreForm.heure || !rencontreForm.lieu.trim()) {
      setError("Merci de renseigner les deux équipes, la date, l'heure et le lieu.");
      return;
    }

    const homeTeam = equipes.find((team) => team.nom === rencontreForm.equipeD);
    const awayTeam = equipes.find((team) => team.nom === rencontreForm.equipeE);

    if (!homeTeam || !awayTeam) {
      setError("Choisis deux équipes réellement créées dans l'administration.");
      return;
    }

    if (homeTeam.id === awayTeam.id) {
      setError("Une équipe ne peut pas jouer contre elle-même.");
      return;
    }

    if (homeTeam.sport !== awayTeam.sport) {
      setError("Une rencontre doit opposer deux équipes du même sport.");
      return;
    }

    if (
      (rencontreForm.statut === "en_cours" || rencontreForm.statut === "termine")
      && (rencontreForm.scoreD === "" || rencontreForm.scoreE === "")
    ) {
      setError("Renseigne les deux scores pour enregistrer une rencontre en cours ou terminée.");
      return;
    }

    const scorePayload =
      rencontreForm.statut === "en_cours" || rencontreForm.statut === "termine"
        ? {
            scoreDomicile: rencontreForm.scoreD !== "" ? Number(rencontreForm.scoreD) : undefined,
            scoreExterieur: rencontreForm.scoreE !== "" ? Number(rencontreForm.scoreE) : undefined
          }
        : {};

    const payload = {
      equipeDomicile: homeTeam.nom,
      equipeExterieur: awayTeam.nom,
      sport: homeTeam.sport,
      date: rencontreForm.date,
      heure: rencontreForm.heure,
      lieu: rencontreForm.lieu.trim(),
      type: rencontreForm.type,
      statut: rencontreForm.statut,
      ...scorePayload
    };

    try {
      if (editRencontre) {
        await updateMatch(request, editRencontre.id, payload);
        showToast(rencontreForm.statut === "termine" ? "Résultat final enregistré" : "Rencontre mise à jour");
      } else {
        await createMatch(request, payload);
        showToast("Rencontre créée avec succès");
      }

      setShowRencontreForm(false);
      await loadMatches();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement impossible";
      setError(message);
    }
  };

  const handleDeleteRencontre = async (id: string) => {
    setDeletingItemKey(`match:${id}`);
    try {
      await deleteMatch(request, id);
      setDeleteIntent(null);
      showToast("Rencontre supprimée");
      await loadMatches();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    } finally {
      setDeletingItemKey(null);
    }
  };

  const saveTraining = async () => {
    if (!trainingForm.equipeId || !trainingForm.jour.trim() || !trainingForm.heure.trim() || !trainingForm.lieu.trim()) {
      setError("Merci de renseigner l'équipe, le jour, l'heure et le lieu de l'entraînement.");
      return;
    }

    try {
      if (editTraining) {
        await updateTraining(request, editTraining.id, trainingForm);
        showToast("Entraînement mis à jour");
      } else {
        await createTraining(request, trainingForm);
        showToast("Entraînement créé avec succès");
      }

      setShowTrainingForm(false);
      setTrainingNotice(null);
      await loadTrainings();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Enregistrement impossible";
      setError(isMissingTrainingTableError(message) ? TRAINING_MIGRATION_MESSAGE : message);
    }
  };

  const handleDeleteTraining = async (id: string) => {
    setDeletingItemKey(`training:${id}`);
    try {
      await deleteTraining(request, id);
      setDeleteIntent(null);
      showToast("Entraînement supprimé");
      setTrainingNotice(null);
      await loadTrainings();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(isMissingTrainingTableError(message) ? TRAINING_MIGRATION_MESSAGE : message);
    } finally {
      setDeletingItemKey(null);
    }
  };

  const handleUpdateChallengeStatus = async (id: string, status: AdminChallenge["status"]) => {
    setDeletingItemKey(`challenge-status:${id}`);
    try {
      await updateAdminChallengeStatus(request, id, status);
      showToast(status === "suspendu" ? "Défi suspendu" : "Défi réactivé");
      await loadChallenges();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Mise à jour impossible";
      setError(message);
    } finally {
      setDeletingItemKey(null);
    }
  };

  const handleDeleteChallenge = async (id: string) => {
    setDeletingItemKey(`challenge:${id}`);
    try {
      await deleteAdminChallenge(request, id);
      setDeleteIntent(null);
      showToast("Défi supprimé");
      await loadChallenges();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Suppression impossible";
      setError(message);
    } finally {
      setDeletingItemKey(null);
    }
  };

  const confirmDeleteIntent = async () => {
    if (!deleteIntent) {
      return;
    }

    if (deleteIntent.kind === "team") {
      await handleDeleteEquipe(deleteIntent.id);
      return;
    }

    if (deleteIntent.kind === "squad") {
      await handleDeleteSquad(deleteIntent.id);
      return;
    }

    if (deleteIntent.kind === "match") {
      await handleDeleteRencontre(deleteIntent.id);
      return;
    }

    if (deleteIntent.kind === "training") {
      await handleDeleteTraining(deleteIntent.id);
      return;
    }

    if (deleteIntent.kind === "challenge") {
      await handleDeleteChallenge(deleteIntent.id);
    }
  };

  const handleReviewSquad = async (squadId: string, approvalStatus: AdminSquad["approvalStatus"]) => {
    try {
      await reviewAdminSquad(request, squadId, approvalStatus);
      showToast(approvalStatus === "approuve" ? "Squad approuvé" : "Squad refusé");
      await loadSquads();
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Validation impossible";
      setError(message);
    }
  };

  const handleReviewTeamJoinRequest = async (
    joinRequestId: string,
    status: Extract<AdminTeamJoinRequest["status"], "approuve" | "refuse">
  ) => {
    setReviewingTeamJoinRequestId(joinRequestId);

    try {
      await reviewTeamJoinRequest(request, joinRequestId, status);
      showToast(status === "approuve" ? "Demande d'équipe approuvée" : "Demande d'équipe refusée");
      await Promise.all([loadTeamJoinRequests(), loadTeams()]);
    } catch (caughtError) {
      const message = caughtError instanceof Error ? caughtError.message : "Validation impossible";
      setError(message);
    } finally {
      setReviewingTeamJoinRequestId(null);
    }
  };

  const activeTeams = useMemo(() => {
    return equipes.filter((team) => team.statut === "actif").length;
  }, [equipes]);

  const pendingTeamJoinRequests = useMemo(() => {
    return teamJoinRequests.filter((item) => item.status === "en_attente").length;
  }, [teamJoinRequests]);

  const approvedSquads = useMemo(() => {
    return squads.filter((squad) => squad.approvalStatus === "approuve").length;
  }, [squads]);

  const totalSquads = squads.length;

  const pendingSquads = useMemo(() => {
    return squads.filter((squad) => squad.approvalStatus === "en_attente").length;
  }, [squads]);

  const completedMatches = useMemo(() => {
    return rencontres.filter((match) => match.statut === "termine").length;
  }, [rencontres]);

  const scheduledTrainings = trainings.length;
  const activeChallenges = useMemo(() => challenges.filter((challenge) => challenge.status === "actif").length, [challenges]);
  const suspendedChallenges = useMemo(() => challenges.filter((challenge) => challenge.status === "suspendu").length, [challenges]);

  const matchReadySports = useMemo(() => {
    const counts = equipes.reduce<Map<string, number>>((accumulator, team) => {
      accumulator.set(team.sport, (accumulator.get(team.sport) ?? 0) + 1);
      return accumulator;
    }, new Map());

    return Array.from(counts.entries())
      .filter(([, count]) => count >= 2)
      .map(([sport]) => sport);
  }, [equipes]);

  const canCreateMatches = matchReadySports.length > 0;
  const teamsTotalPages = Math.max(1, Math.ceil(equipes.length / TEAMS_PER_PAGE));
  const paginatedTeams = useMemo(() => {
    const startIndex = (teamsPage - 1) * TEAMS_PER_PAGE;
    return equipes.slice(startIndex, startIndex + TEAMS_PER_PAGE);
  }, [equipes, teamsPage]);
  const squadsTotalPages = Math.max(1, Math.ceil(squads.length / SQUADS_PER_PAGE));
  const paginatedSquads = useMemo(() => {
    const startIndex = (squadsPage - 1) * SQUADS_PER_PAGE;
    return squads.slice(startIndex, startIndex + SQUADS_PER_PAGE);
  }, [squads, squadsPage]);
  const matchesTotalPages = Math.max(1, Math.ceil(rencontres.length / MATCHES_PER_PAGE));
  const paginatedMatches = useMemo(() => {
    const startIndex = (matchesPage - 1) * MATCHES_PER_PAGE;
    return rencontres.slice(startIndex, startIndex + MATCHES_PER_PAGE);
  }, [matchesPage, rencontres]);
  const trainingsTotalPages = Math.max(1, Math.ceil(trainings.length / TRAININGS_PER_PAGE));
  const paginatedTrainings = useMemo(() => {
    const startIndex = (trainingsPage - 1) * TRAININGS_PER_PAGE;
    return trainings.slice(startIndex, startIndex + TRAININGS_PER_PAGE);
  }, [trainings, trainingsPage]);
  const challengesTotalPages = Math.max(1, Math.ceil(challenges.length / CHALLENGES_PER_PAGE));
  const paginatedChallenges = useMemo(() => {
    const startIndex = (challengesPage - 1) * CHALLENGES_PER_PAGE;
    return challenges.slice(startIndex, startIndex + CHALLENGES_PER_PAGE);
  }, [challenges, challengesPage]);

  const selectedHomeTeam = useMemo(
    () => equipes.find((team) => team.nom === rencontreForm.equipeD) ?? null,
    [equipes, rencontreForm.equipeD]
  );

  const selectedAwayTeam = useMemo(
    () => equipes.find((team) => team.nom === rencontreForm.equipeE) ?? null,
    [equipes, rencontreForm.equipeE]
  );

  const homeTeamOptions = useMemo(() => {
    return equipes.filter(
      (team) =>
        matchReadySports.includes(team.sport) ||
        team.nom === rencontreForm.equipeD ||
        team.nom === rencontreForm.equipeE
    );
  }, [equipes, matchReadySports, rencontreForm.equipeD, rencontreForm.equipeE]);

  const awayTeamOptions = useMemo(() => {
    if (!selectedHomeTeam) {
      return [];
    }

    return equipes.filter((team) => team.id !== selectedHomeTeam.id && team.sport === selectedHomeTeam.sport);
  }, [equipes, selectedHomeTeam]);

  const derivedMatchSport = useMemo(() => {
    if (selectedHomeTeam && selectedAwayTeam && selectedHomeTeam.sport === selectedAwayTeam.sport) {
      return selectedHomeTeam.sport;
    }

    return selectedHomeTeam?.sport ?? selectedAwayTeam?.sport ?? rencontreForm.sport;
  }, [rencontreForm.sport, selectedAwayTeam, selectedHomeTeam]);

  useEffect(() => {
    if (autoOpenedPendingSquadsRef.current) {
      return;
    }

    if (loadingSquads || loadingTeamJoinRequests) {
      return;
    }

    if (pendingTeamJoinRequests > 0) {
      setSection("demandes_equipes");
    } else if (pendingSquads > 0) {
      setSection("squads");
    }

    autoOpenedPendingSquadsRef.current = true;
  }, [loadingSquads, loadingTeamJoinRequests, pendingSquads, pendingTeamJoinRequests]);

  useEffect(() => {
    setTeamsPage((page) => Math.min(page, teamsTotalPages));
  }, [teamsTotalPages]);

  useEffect(() => {
    setSquadsPage((page) => Math.min(page, squadsTotalPages));
  }, [squadsTotalPages]);

  useEffect(() => {
    setMatchesPage((page) => Math.min(page, matchesTotalPages));
  }, [matchesTotalPages]);

  useEffect(() => {
    setTrainingsPage((page) => Math.min(page, trainingsTotalPages));
  }, [trainingsTotalPages]);

  useEffect(() => {
    setChallengesPage((page) => Math.min(page, challengesTotalPages));
  }, [challengesTotalPages]);

  return (
    <div className="space-y-6 relative">
      <div className="fixed top-6 right-6 z-50 flex max-w-sm flex-col gap-3">
        {toast ? (
          <div className="px-5 py-3 rounded-xl bg-emerald-500 text-white text-sm font-medium flex items-center gap-2 shadow-lg">
            <i className="ri-checkbox-circle-line text-lg"></i>
            {toast}
          </div>
        ) : null}

        {deleteIntent ? (
          <div className="rounded-2xl border border-red-100 bg-white p-4 shadow-[0_18px_40px_rgba(15,23,42,0.14)]">
            <div className="flex items-start gap-3">
              <div className="mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-500">
                <i className="ri-delete-bin-line text-lg"></i>
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#1A1A1A]">Confirmation de suppression</p>
                <p className="mt-1 text-sm text-gray-600">{deleteIntent.message}</p>
                <p className="mt-1 text-xs font-medium text-gray-400 truncate">{deleteIntent.name}</p>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <button
                    onClick={() => setDeleteIntent(null)}
                    disabled={deletingItemKey === `${deleteIntent.kind}:${deleteIntent.id}`}
                    className="rounded-xl border border-gray-200 px-3.5 py-2 text-sm font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={() => void confirmDeleteIntent()}
                    disabled={deletingItemKey === `${deleteIntent.kind}:${deleteIntent.id}`}
                    className="inline-flex items-center gap-2 rounded-xl bg-red-500 px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {deletingItemKey === `${deleteIntent.kind}:${deleteIntent.id}` ? (
                      <i className="ri-loader-4-line animate-spin"></i>
                    ) : (
                      <i className="ri-delete-bin-line"></i>
                    )}
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1A1A1A]">Équipes, Squads, Rencontres &amp; Entraînements</h1>
          <p className="text-gray-500 text-sm mt-1">L&apos;admin gère ici les effectifs, valide les squads et pilote le calendrier sportif ainsi que le planning des entraînements</p>
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => {
              void loadTeams();
              void loadTeamJoinRequests();
              void loadSquads();
              void loadMatches();
              void loadTrainings();
              void loadChallenges();
            }}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-gray-600 text-sm font-medium border border-gray-200 hover:border-[#E8521A] hover:text-[#E8521A] transition-colors whitespace-nowrap"
          >
            <i className="ri-refresh-line"></i>
            Actualiser
          </button>
          {section === "squads" ? (
            <div className="px-4 py-2.5 rounded-xl bg-orange-50 text-[#9A3E18] text-sm font-medium border border-orange-100">
              {totalSquads} squad{totalSquads > 1 ? "s" : ""} au total
              {pendingSquads > 0 ? ` · ${pendingSquads} en attente de validation` : ""}
            </div>
          ) : section === "defis" ? (
            <div className="px-4 py-2.5 rounded-xl bg-orange-50 text-[#9A3E18] text-sm font-medium border border-orange-100">
              Gestion des défis membres
            </div>
          ) : section === "demandes_equipes" ? (
            <div className="px-4 py-2.5 rounded-xl bg-teal-50 text-[#116b5d] text-sm font-medium border border-teal-100">
              {pendingTeamJoinRequests} demande{pendingTeamJoinRequests > 1 ? "s" : ""} à traiter
            </div>
          ) : (
            <button
              onClick={section === "equipes" ? openNewEquipe : section === "rencontres" ? openNewRencontre : openNewTraining}
              disabled={(section === "rencontres" && !canCreateMatches) || (section === "entrainements" && equipes.length === 0)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-colors whitespace-nowrap ${
                (section === "rencontres" && !canCreateMatches) || (section === "entrainements" && equipes.length === 0)
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-[#E8521A] text-white hover:bg-[#d44a16]"
              }`}
            >
              <i className="ri-add-line"></i>
              {section === "equipes" ? "Créer une équipe" : section === "rencontres" ? "Nouvelle rencontre" : "Nouvel entraînement"}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-8 gap-4">
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#1A1A1A]">{equipes.length}</div>
          <div className="text-sm text-gray-500 mt-1">Équipes</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#1A8C7A]">{activeTeams}</div>
          <div className="text-sm text-gray-500 mt-1">Équipes actives</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#E8521A]">{approvedSquads}</div>
          <div className="text-sm text-gray-500 mt-1">Squads approuvés</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-amber-600">{pendingSquads}</div>
          <div className="text-sm text-gray-500 mt-1">Squads à valider</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-gray-500">{completedMatches}</div>
          <div className="text-sm text-gray-500 mt-1">Rencontres terminées</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-[#1A8C7A]">{scheduledTrainings}</div>
          <div className="text-sm text-gray-500 mt-1">Entraînements</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-purple-600">{activeChallenges}</div>
          <div className="text-sm text-gray-500 mt-1">Défis actifs</div>
        </div>
        <div className="bg-white rounded-2xl p-4 border border-gray-100">
          <div className="text-2xl font-bold text-amber-600">{suspendedChallenges}</div>
          <div className="text-sm text-gray-500 mt-1">Défis suspendus</div>
        </div>
      </div>

      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        <button
          onClick={() => setSection("equipes")}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            section === "equipes" ? "bg-white text-[#E8521A]" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <i className="ri-shield-star-line mr-2"></i>Équipes ({equipes.length})
        </button>
        <button
          onClick={() => setSection("demandes_equipes")}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            section === "demandes_equipes" ? "bg-white text-[#E8521A]" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <i className="ri-user-follow-line mr-2"></i>Demandes équipes ({pendingTeamJoinRequests})
        </button>
        <button
          onClick={() => setSection("squads")}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            section === "squads" ? "bg-white text-[#E8521A]" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <i className="ri-team-line mr-2"></i>Squads ({totalSquads})
        </button>
        <button
          onClick={() => setSection("rencontres")}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            section === "rencontres" ? "bg-white text-[#E8521A]" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <i className="ri-sword-line mr-2"></i>Rencontres ({rencontres.length})
        </button>
        <button
          onClick={() => setSection("entrainements")}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            section === "entrainements" ? "bg-white text-[#E8521A]" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <i className="ri-calendar-schedule-line mr-2"></i>Entraînements ({trainings.length})
        </button>
        <button
          onClick={() => setSection("defis")}
          className={`px-5 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
            section === "defis" ? "bg-white text-[#E8521A]" : "text-gray-500 hover:text-gray-700"
          }`}
        >
          <i className="ri-trophy-line mr-2"></i>Défis ({challenges.length})
        </button>
      </div>

      {section === "equipes" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {loadingTeams ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse"></div>
            ))
          ) : paginatedTeams.map((equipe) => (
            <div key={equipe.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-colors">
              <div className="h-2" style={{ backgroundColor: equipe.couleur }}></div>
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${equipe.couleur}20` }}>
                      <i className="ri-shield-star-fill text-xl" style={{ color: equipe.couleur }}></i>
                    </div>
                    <div>
                      <h3 className="font-bold text-[#1A1A1A] text-sm">{equipe.nom}</h3>
                      <span className="text-xs text-gray-500">{equipe.sport}</span>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${equipe.statut === "actif" ? "bg-emerald-100 text-emerald-600" : "bg-gray-100 text-gray-500"}`}>
                    {equipe.statut === "actif" ? "Actif" : "Inactif"}
                  </span>
                </div>

                <div className="space-y-1.5 mb-4">
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <i className="ri-user-star-line text-[#E8521A]"></i>
                    <span>Entraîneur : <span className="font-medium text-gray-700">{equipe.entraineur || "—"}</span></span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-500">
                    <i className="ri-team-line text-[#E8521A]"></i>
                    <span><span className="font-medium text-gray-700">{equipe.joueurs.length}</span> joueur{equipe.joueurs.length > 1 ? "s" : ""}</span>
                  </div>
                </div>

                {equipe.joueurs.length > 0 ? (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {equipe.joueurs.slice(0, 5).map((joueur) => (
                        <span key={joueur.id} className="text-xs bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full text-gray-600">
                          #{joueur.numero} {joueur.nom.split(" ")[0]}
                        </span>
                      ))}
                      {equipe.joueurs.length > 5 ? (
                        <span className="text-xs bg-orange-50 text-[#E8521A] px-2 py-0.5 rounded-full">+{equipe.joueurs.length - 5}</span>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <div className="flex gap-2">
                  <button
                    onClick={() => setSelectedEquipe(equipe)}
                    className="flex-1 py-2 rounded-xl border border-gray-200 text-gray-600 text-xs font-medium hover:bg-gray-50 transition-colors"
                  >
                    <i className="ri-eye-line mr-1"></i>Détails
                  </button>
                  <button
                    onClick={() => openEditEquipe(equipe)}
                    className="flex-1 py-2 rounded-xl bg-orange-50 text-[#E8521A] text-xs font-medium hover:bg-orange-100 transition-colors"
                  >
                    <i className="ri-edit-line mr-1"></i>Modifier
                  </button>
                  <button
                    onClick={() => requestDeleteEquipe(equipe)}
                    disabled={deletingItemKey === `team:${equipe.id}`}
                    aria-label={`Supprimer l'équipe ${equipe.nom}`}
                    title="Supprimer l'équipe"
                    className="w-9 h-9 flex items-center justify-center rounded-xl bg-red-50 text-red-400 hover:bg-red-100 transition-colors flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className={`${deletingItemKey === `team:${equipe.id}` ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} text-sm`}></i>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {!loadingTeams && equipes.length === 0 ? (
            <div className="col-span-3 text-center py-16 text-gray-400">
              <i className="ri-shield-star-line text-4xl mb-3 block"></i>
              <p className="text-sm">Aucune équipe créée. Cliquez sur &quot;Créer une équipe&quot; pour commencer.</p>
            </div>
          ) : null}

          {!loadingTeams && equipes.length > 0 ? (
            <div className="md:col-span-2 xl:col-span-3">
              <PaginationControls
                currentPage={teamsPage}
                totalPages={teamsTotalPages}
                onPageChange={setTeamsPage}
                itemLabel="équipes"
              />
            </div>
          ) : null}
        </div>
      ) : section === "demandes_equipes" ? (
        <div className="space-y-3">
          {loadingTeamJoinRequests ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-5 h-32 animate-pulse"></div>
            ))
          ) : teamJoinRequests.map((joinRequest) => {
            const isPending = joinRequest.status === "en_attente";
            const statusClass =
              joinRequest.status === "approuve"
                ? "bg-emerald-100 text-emerald-600"
                : joinRequest.status === "refuse"
                  ? "bg-red-100 text-red-500"
                  : "bg-amber-100 text-amber-700";
            const statusLabel =
              joinRequest.status === "approuve"
                ? "Approuvée"
                : joinRequest.status === "refuse"
                  ? "Refusée"
                  : "En attente";

            return (
              <div key={joinRequest.id} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-orange-200 transition-colors">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                  <div className="flex min-w-0 items-start gap-4">
                    <img
                      src={joinRequest.member.avatar ?? ""}
                      alt={joinRequest.member.nom}
                      className="h-12 w-12 flex-shrink-0 rounded-2xl object-cover"
                    />
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-bold text-[#1A1A1A]">{joinRequest.member.nom}</h3>
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClass}`}>{statusLabel}</span>
                      </div>
                      <p className="mt-1 text-sm text-gray-500">
                        Demande pour <span className="font-semibold text-gray-700">{joinRequest.team.nom}</span> · {joinRequest.team.sport}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2 text-xs text-gray-400">
                        <span>{joinRequest.member.email ?? "Email non renseigné"}</span>
                        <span>{joinRequest.member.matricule ?? "Matricule non renseigné"}</span>
                        <span>{joinRequest.member.departement ?? "Direction non renseignée"}</span>
                        <span>Demandée le {new Date(joinRequest.createdAt).toLocaleDateString("fr-FR")}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => void handleReviewTeamJoinRequest(joinRequest.id, "refuse")}
                      disabled={!isPending || reviewingTeamJoinRequestId === joinRequest.id}
                      className="inline-flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-3.5 py-2 text-sm font-medium text-red-500 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {reviewingTeamJoinRequestId === joinRequest.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-close-line"></i>}
                      Refuser
                    </button>
                    <button
                      type="button"
                      onClick={() => void handleReviewTeamJoinRequest(joinRequest.id, "approuve")}
                      disabled={!isPending || reviewingTeamJoinRequestId === joinRequest.id}
                      className="inline-flex items-center gap-2 rounded-xl bg-[#1A8C7A] px-3.5 py-2 text-sm font-medium text-white transition-colors hover:bg-[#157a6a] disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {reviewingTeamJoinRequestId === joinRequest.id ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                      Approuver
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {!loadingTeamJoinRequests && teamJoinRequests.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white py-16 text-center text-gray-400">
              <i className="ri-user-follow-line mb-3 block text-4xl"></i>
              <p className="text-sm">Aucune demande d&apos;intégration à une équipe pour le moment.</p>
            </div>
          ) : null}
        </div>
      ) : section === "squads" ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {loadingSquads ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 h-56 animate-pulse"></div>
            ))
          ) : paginatedSquads.map((squad) => {
            const statusConfig = statutSquadConfig[squad.status];
            const approvalConfig = validationSquadConfig[squad.approvalStatus];
            const accent = getSquadAccent(squad.color);

            return (
              <div key={squad.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-orange-200 transition-colors">
                <div className="h-2" style={{ backgroundColor: accent }}></div>
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
                        style={{ backgroundColor: accent }}
                      >
                        <i className={`${squad.icon} text-white text-xl`}></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-[#1A1A1A]">{squad.name}</h3>
                        <p className="text-sm text-gray-500">{squad.category}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${approvalConfig.cls}`}>{approvalConfig.label}</span>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${statusConfig.cls}`}>{statusConfig.label}</span>
                    </div>
                  </div>

                  <p className="text-sm text-gray-600 mb-4">{squad.description}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                    <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
                      <div className="text-xs text-gray-400 mb-1">Créateur</div>
                      <div className="text-sm font-medium text-gray-700">{squad.creator.name}</div>
                      <div className="text-xs text-gray-500">{squad.creator.email}</div>
                    </div>
                    <div className="rounded-xl bg-gray-50 border border-gray-100 px-3 py-2.5">
                      <div className="text-xs text-gray-400 mb-1">Effectif</div>
                      <div className="text-sm font-medium text-gray-700">
                        {squad.members.length}/{squad.maxMembers} membre{squad.maxMembers > 1 ? "s" : ""}
                      </div>
                      <div className="text-xs text-gray-500">
                        Créé le {new Date(squad.createdAt).toLocaleDateString("fr-FR")}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex -space-x-2">
                      {squad.members.slice(0, 4).map((member) =>
                        member.avatar ? (
                          <img
                            key={member.id}
                            src={member.avatar}
                            alt={member.name}
                            className="w-9 h-9 rounded-full border-2 border-white object-cover"
                          />
                        ) : (
                          <div key={member.id} className="w-9 h-9 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-gray-400">
                            <i className="ri-user-line text-sm"></i>
                          </div>
                        )
                      )}
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => requestDeleteSquad(squad)}
                        disabled={deletingItemKey === `squad:${squad.id}`}
                        aria-label={`Supprimer le squad ${squad.name}`}
                        title="Supprimer le squad"
                        className="w-10 h-10 flex items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <i className={`${deletingItemKey === `squad:${squad.id}` ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} text-base`}></i>
                      </button>
                      <button
                        onClick={() => void handleReviewSquad(squad.id, "refuse")}
                        disabled={squad.approvalStatus === "refuse" || deletingItemKey === `squad:${squad.id}`}
                        className="px-3.5 py-2 rounded-xl border border-red-100 bg-red-50 text-red-500 text-sm font-medium hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Refuser
                      </button>
                      <button
                        onClick={() => void handleReviewSquad(squad.id, "approuve")}
                        disabled={squad.approvalStatus === "approuve" || deletingItemKey === `squad:${squad.id}`}
                        className="px-3.5 py-2 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        Approuver
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {!loadingSquads && squads.length === 0 ? (
            <div className="xl:col-span-2 text-center py-16 text-gray-400">
              <i className="ri-team-line text-4xl mb-3 block"></i>
              <p className="text-sm">Aucun squad proposé pour le moment.</p>
            </div>
          ) : null}

          {!loadingSquads && squads.length > 0 ? (
            <div className="xl:col-span-2">
              <PaginationControls
                currentPage={squadsPage}
                totalPages={squadsTotalPages}
                onPageChange={setSquadsPage}
                itemLabel="squads"
              />
            </div>
          ) : null}
        </div>
      ) : section === "rencontres" ? (
        <div className="space-y-3">
          {loadingMatches ? (
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-5 h-32 animate-pulse"></div>
            ))
          ) : paginatedMatches.map((rencontre) => {
            const typeConfig = typeRencontreConfig[rencontre.type];
            const statutConfig = statutRencontreConfig[rencontre.statut];
            const isCompleted = rencontre.statut === "termine";

            return (
              <div key={rencontre.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-orange-200 transition-colors">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="text-center flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                        <i className="ri-shield-star-fill text-[#E8521A]"></i>
                      </div>
                      <div className="text-xs font-semibold text-[#1A1A1A] mt-1 max-w-[80px] truncate">
                        {rencontre.equipeDomicile.split(" ")[0]}
                      </div>
                    </div>

                    <div className="flex-1 text-center">
                      {isCompleted && rencontre.scoreDomicile !== null && rencontre.scoreDomicile !== undefined ? (
                        <div className="text-2xl font-black text-[#1A1A1A]">
                          {rencontre.scoreDomicile} <span className="text-gray-300">—</span> {rencontre.scoreExterieur}
                        </div>
                      ) : (
                        <div className="text-lg font-bold text-gray-300">VS</div>
                      )}
                      <div className="flex items-center justify-center gap-2 mt-1 flex-wrap">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${typeConfig.cls}`}>{typeConfig.label}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statutConfig.cls}`}>{statutConfig.label}</span>
                      </div>
                    </div>

                    <div className="text-center flex-shrink-0">
                      <div className="w-10 h-10 rounded-xl bg-teal-100 flex items-center justify-center">
                        <i className="ri-shield-star-line text-[#1A8C7A]"></i>
                      </div>
                      <div className="text-xs font-semibold text-[#1A1A1A] mt-1 max-w-[80px] truncate">
                        {rencontre.equipeExterieur.split(" ")[0]}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-1 text-xs text-gray-500 flex-shrink-0">
                    <span className="flex items-center gap-1"><i className="ri-calendar-line text-[#E8521A]"></i>{rencontre.date}</span>
                    <span className="flex items-center gap-1"><i className="ri-time-line text-[#E8521A]"></i>{rencontre.heure}</span>
                    <span className="flex items-center gap-1"><i className="ri-map-pin-line text-[#E8521A]"></i>{rencontre.lieu}</span>
                    <span className="flex items-center gap-1"><i className="ri-football-line text-[#E8521A]"></i>{rencontre.sport}</span>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                    <button
                      onClick={() => openResultRencontre(rencontre)}
                      className="inline-flex items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                    >
                      <i className="ri-medal-line text-sm"></i>
                      {isCompleted ? "Mettre à jour le résultat" : "Saisir le résultat"}
                    </button>
                    <button
                      onClick={() => openEditRencontre(rencontre)}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-[#E8521A] transition-colors"
                    >
                      <i className="ri-edit-line text-sm"></i>
                    </button>
                    <button
                      onClick={() => requestDeleteMatch(rencontre)}
                      disabled={deletingItemKey === `match:${rencontre.id}`}
                      className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className={`${deletingItemKey === `match:${rencontre.id}` ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} text-sm`}></i>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          {!loadingMatches && rencontres.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-sword-line text-4xl mb-3 block"></i>
              <p className="text-sm">Aucune rencontre planifiée. Cliquez sur &quot;Nouvelle rencontre&quot; pour commencer.</p>
              {!canCreateMatches ? (
                <p className="text-xs text-gray-400 mt-2">
                  Il faut au moins deux équipes créées dans le même sport pour publier une rencontre.
                </p>
              ) : null}
            </div>
          ) : null}

          {!loadingMatches && rencontres.length > 0 ? (
            <PaginationControls
              currentPage={matchesPage}
              totalPages={matchesTotalPages}
              onPageChange={setMatchesPage}
              itemLabel="rencontres"
            />
          ) : null}
        </div>
      ) : section === "defis" ? (
        <div className="space-y-3">
          {loadingChallenges ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-5 h-44 animate-pulse"></div>
            ))
          ) : paginatedChallenges.map((challenge) => (
            <div key={challenge.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-orange-200 transition-colors">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4 min-w-0 flex-1">
                  <div className={`w-12 h-12 rounded-xl ${challenge.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <i className={`${challenge.icon} ${challenge.color} text-xl`}></i>
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-bold text-[#1A1A1A]">{challenge.title}</h3>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${challenge.status === "actif" ? "bg-emerald-100 text-emerald-600" : "bg-amber-100 text-amber-700"}`}>
                        {challenge.status === "actif" ? "Actif" : "Suspendu"}
                      </span>
                      <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-gray-100 text-gray-600">
                        {challenge.createdBySquadLeader ? "Défi de squad" : "Défi individuel"}
                      </span>
                    </div>
                    <p className="mt-2 text-sm text-gray-600">{challenge.description}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-[#E8521A]">
                        <i className="ri-football-line"></i>
                        {challenge.sport}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 text-purple-700">
                        <i className="ri-trophy-line"></i>
                        {challenge.target.toLocaleString("fr-FR")} {challenge.unit}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[#1A8C7A]">
                        <i className="ri-user-line"></i>
                        {challenge.participants} participant{challenge.participants > 1 ? "s" : ""}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-gray-600">
                        <i className="ri-calendar-line"></i>
                        Fin le {new Date(`${challenge.deadline}T12:00:00`).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                    <div className="mt-3 text-xs text-gray-500">
                      Créé par <span className="font-medium text-gray-700">{challenge.creatorName ?? "Membre introuvable"}</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                  <button
                    onClick={() => void handleUpdateChallengeStatus(challenge.id, challenge.status === "actif" ? "suspendu" : "actif")}
                    disabled={deletingItemKey === `challenge-status:${challenge.id}` || deletingItemKey === `challenge:${challenge.id}`}
                    className={`px-3.5 py-2 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      challenge.status === "actif"
                        ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
                        : "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                    }`}
                  >
                    <i className={`${deletingItemKey === `challenge-status:${challenge.id}` ? "ri-loader-4-line animate-spin" : challenge.status === "actif" ? "ri-pause-circle-line" : "ri-play-circle-line"} mr-1.5`}></i>
                    {challenge.status === "actif" ? "Suspendre" : "Réactiver"}
                  </button>
                  <button
                    onClick={() => requestDeleteChallenge(challenge)}
                    disabled={deletingItemKey === `challenge:${challenge.id}` || deletingItemKey === `challenge-status:${challenge.id}`}
                    className="w-10 h-10 flex items-center justify-center rounded-xl border border-red-100 bg-red-50 text-red-500 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label={`Supprimer le défi ${challenge.title}`}
                  >
                    <i className={`${deletingItemKey === `challenge:${challenge.id}` ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} text-base`}></i>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {!loadingChallenges && challenges.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-trophy-line text-4xl mb-3 block"></i>
              <p className="text-sm">Aucun défi créé pour le moment.</p>
            </div>
          ) : null}

          {!loadingChallenges && challenges.length > 0 ? (
            <PaginationControls
              currentPage={challengesPage}
              totalPages={challengesTotalPages}
              onPageChange={setChallengesPage}
              itemLabel="défis"
            />
          ) : null}
        </div>
      ) : (
        <div className="space-y-3">
          {trainingNotice ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              {trainingNotice}
            </div>
          ) : null}

          {loadingTrainings ? (
            Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="bg-white rounded-2xl border border-gray-100 p-5 h-28 animate-pulse"></div>
            ))
          ) : paginatedTrainings.map((training) => (
            <div key={training.id} className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-orange-200 transition-colors">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-start gap-4 min-w-0">
                  <div className="w-12 h-12 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-calendar-schedule-line text-[#1A8C7A] text-xl"></i>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-bold text-[#1A1A1A]">{training.equipeNom}</h3>
                    <p className="text-sm text-gray-500">{training.sport}</p>
                    <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2.5 py-1 text-[#E8521A]">
                        <i className="ri-calendar-line"></i>
                        {training.jour}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-teal-50 px-2.5 py-1 text-[#1A8C7A]">
                        <i className="ri-time-line"></i>
                        {training.heure}
                      </span>
                      <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-gray-600">
                        <i className="ri-map-pin-line"></i>
                        {training.lieu}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => openEditTraining(training)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-gray-100 text-gray-600 hover:bg-orange-100 hover:text-[#E8521A] transition-colors"
                  >
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => requestDeleteTraining(training)}
                    disabled={deletingItemKey === `training:${training.id}`}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className={`${deletingItemKey === `training:${training.id}` ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"} text-sm`}></i>
                  </button>
                </div>
              </div>
            </div>
          ))}

          {!loadingTrainings && trainings.length === 0 ? (
            <div className="text-center py-16 text-gray-400">
              <i className="ri-calendar-schedule-line text-4xl mb-3 block"></i>
              <p className="text-sm">Aucun entraînement planifié pour le moment.</p>
              {equipes.length === 0 ? (
                <p className="text-xs text-gray-400 mt-2">
                  Il faut d&apos;abord créer une équipe avant de publier un entraînement.
                </p>
              ) : null}
            </div>
          ) : null}

          {!loadingTrainings && trainings.length > 0 ? (
            <PaginationControls
              currentPage={trainingsPage}
              totalPages={trainingsTotalPages}
              onPageChange={setTrainingsPage}
              itemLabel="entraînements"
            />
          ) : null}
        </div>
      )}

      {showEquipeForm ? (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowEquipeForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-[#1A1A1A] text-lg">{editEquipe ? "Modifier l'équipe" : "Créer une équipe"}</h3>
              <button onClick={() => setShowEquipeForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-information-line text-[#E8521A]"></i>Informations générales
                </h4>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom de l&apos;équipe *</label>
                    <input
                      type="text"
                      value={equipeForm.nom}
                      onChange={(event) => setEquipeForm({ ...equipeForm, nom: event.target.value })}
                      placeholder="Ex: Lions du Plateau..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Sport</label>
                      <select
                        value={equipeForm.sport}
                        onChange={(event) => setEquipeForm({ ...equipeForm, sport: event.target.value })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                      >
                        {SPORTS.map((sport) => <option key={sport} value={sport}>{sport}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Statut</label>
                      <select
                        value={equipeForm.statut}
                        onChange={(event) => setEquipeForm({ ...equipeForm, statut: event.target.value as AdminTeam["statut"] })}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                      >
                        <option value="actif">Actif</option>
                        <option value="inactif">Inactif</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Entraîneur *</label>
                    <input
                      type="text"
                      value={equipeForm.entraineur}
                      onChange={(event) => setEquipeForm({ ...equipeForm, entraineur: event.target.value })}
                      placeholder="Nom de l'entraîneur..."
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Couleur de l&apos;équipe</label>
                    <div className="flex gap-2 flex-wrap">
                      {COULEURS.map((couleur) => (
                        <button
                          key={couleur}
                          type="button"
                          onClick={() => setEquipeForm({ ...equipeForm, couleur })}
                          className={`w-8 h-8 rounded-full transition-all ${equipeForm.couleur === couleur ? "ring-2 ring-offset-2 ring-gray-400 scale-110" : ""}`}
                          style={{ backgroundColor: couleur }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <i className="ri-team-line text-[#E8521A]"></i>Joueurs ({joueurs.length})
                </h4>
                <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.45fr)_minmax(300px,0.8fr)] gap-4 mb-4">
                  <div className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
                    <div className="p-4 border-b border-gray-100">
                      <div className="relative">
                        <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          value={membersCatalogSearch}
                          onChange={(event) => {
                            setMembersCatalogSearch(event.target.value);
                            setMembersCatalogPage(1);
                          }}
                          placeholder="Rechercher un membre actif par nom, email ou matricule..."
                          className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                        />
                      </div>
                      <p className="mt-3 text-xs text-gray-400">
                        L&apos;équipe doit être composée de vrais membres déjà inscrits et actifs. Une fois ajoutés ici, ils auront accès au chat privé de l&apos;équipe.
                      </p>
                    </div>

                    <div className="divide-y divide-gray-50">
                      {loadingMembersCatalog ? (
                        Array.from({ length: 4 }).map((_, index) => (
                          <div key={index} className="px-4 py-4">
                            <div className="h-16 rounded-2xl bg-gray-100 animate-pulse"></div>
                          </div>
                        ))
                      ) : membersCatalog.length > 0 ? (
                        membersCatalog.map((member) => {
                          const alreadyAdded = joueurs.some((joueur) => joueur.userId === member.id);
                          const isSelected = selectedTeamMember?.id === member.id;

                          return (
                            <div key={member.id} className="px-4 py-3 flex items-center gap-3">
                              <img src={member.avatar ?? ""} alt={member.nom} className="w-11 h-11 rounded-2xl object-cover flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-[#1A1A1A] truncate">{member.prenom} {member.nom}</span>
                                  <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-600">Actif</span>
                                </div>
                                <div className="text-xs text-gray-500 truncate">{member.email}</div>
                                <div className="text-[11px] text-gray-400 mt-1 flex items-center gap-2 flex-wrap">
                                  <span>{member.matricule ?? "Matricule non renseigné"}</span>
                                  <span>{member.departement ?? "Département non renseigné"}</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  if (alreadyAdded) {
                                    return;
                                  }

                                  setSelectedTeamMember(member);
                                  setJoueurForm((current) => ({
                                    ...current,
                                    numero: current.numero || String(joueurs.length + 1)
                                  }));
                                }}
                                disabled={alreadyAdded}
                                className={`px-3 py-2 rounded-xl text-xs font-medium transition-colors whitespace-nowrap ${
                                  alreadyAdded
                                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                                    : isSelected
                                      ? "bg-orange-100 text-[#E8521A]"
                                      : "bg-[#E8521A] text-white hover:bg-[#d44a16]"
                                }`}
                              >
                                {alreadyAdded ? "Déjà ajouté" : isSelected ? "Sélectionné" : "Choisir"}
                              </button>
                            </div>
                          );
                        })
                      ) : (
                        <div className="px-6 py-10 text-center text-sm text-gray-400">
                          <i className="ri-user-search-line text-3xl mb-3 block"></i>
                          Aucun membre actif ne correspond à votre recherche.
                        </div>
                      )}
                    </div>

                    <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between gap-3 flex-wrap bg-gray-50/70">
                      <div className="text-xs text-gray-400">
                        {membersCatalogTotal} membre{membersCatalogTotal > 1 ? "s" : ""} actif{membersCatalogTotal > 1 ? "s" : ""}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setMembersCatalogPage((current) => Math.max(current - 1, 1))}
                          disabled={membersCatalogPage <= 1 || loadingMembersCatalog}
                          className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#E8521A] hover:text-[#E8521A] transition-colors"
                        >
                          <i className="ri-arrow-left-s-line"></i>
                        </button>
                        <span className="text-xs text-gray-500">
                          Page {membersCatalogPage} / {membersCatalogTotalPages}
                        </span>
                        <button
                          type="button"
                          onClick={() => setMembersCatalogPage((current) => Math.min(current + 1, membersCatalogTotalPages))}
                          disabled={membersCatalogPage >= membersCatalogTotalPages || loadingMembersCatalog}
                          className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-medium text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed hover:border-[#E8521A] hover:text-[#E8521A] transition-colors"
                        >
                          <i className="ri-arrow-right-s-line"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-gray-100 bg-gray-50/80 p-4">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <h5 className="text-sm font-semibold text-[#1A1A1A]">Ajouter le membre sélectionné</h5>
                        <p className="text-xs text-gray-400 mt-1">Choisissez un membre à gauche, puis renseignez son poste et son numéro.</p>
                      </div>
                      {selectedTeamMember ? (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTeamMember(null);
                            setJoueurForm({ poste: "", numero: "" });
                          }}
                          className="w-8 h-8 flex items-center justify-center rounded-xl hover:bg-white text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <i className="ri-close-line"></i>
                        </button>
                      ) : null}
                    </div>

                    {selectedTeamMember ? (
                      <div className="mt-4 p-3 rounded-2xl bg-white border border-orange-100 flex items-center gap-3">
                        <img src={selectedTeamMember.avatar ?? ""} alt={selectedTeamMember.nom} className="w-12 h-12 rounded-2xl object-cover flex-shrink-0" />
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-[#1A1A1A] truncate">{selectedTeamMember.prenom} {selectedTeamMember.nom}</div>
                          <div className="text-xs text-gray-500 truncate">{selectedTeamMember.email}</div>
                          <div className="text-[11px] text-gray-400 mt-1">
                            {selectedTeamMember.departement ?? "Département non renseigné"}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 p-4 rounded-2xl border border-dashed border-gray-200 text-center text-sm text-gray-400 bg-white">
                        Sélectionnez d&apos;abord un membre dans la liste pour l&apos;ajouter à l&apos;équipe.
                      </div>
                    )}

                    <div className="grid grid-cols-[96px_minmax(0,1fr)] gap-2 mt-4">
                      <input
                        type="number"
                        value={joueurForm.numero}
                        onChange={(event) => setJoueurForm({ ...joueurForm, numero: event.target.value })}
                        placeholder="#"
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                      />
                      <input
                        type="text"
                        value={joueurForm.poste}
                        onChange={(event) => setJoueurForm({ ...joueurForm, poste: event.target.value })}
                        placeholder="Poste du membre dans l'équipe..."
                        className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            addJoueur();
                          }
                        }}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={addJoueur}
                      disabled={!selectedTeamMember || !joueurForm.poste.trim()}
                      className="w-full mt-4 px-4 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-medium hover:bg-[#d44a16] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <i className="ri-add-line mr-2"></i>
                      Ajouter à l&apos;équipe
                    </button>
                  </div>
                </div>
                <p className="mb-3 text-xs text-gray-400">
                  Chaque joueur ajouté ici est relié à un vrai compte membre, ce qui ouvre automatiquement l&apos;accès au réseau privé de discussion de l&apos;équipe.
                </p>
                {joueurs.some((joueur) => !joueur.userId) ? (
                  <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-700">
                    Cette équipe contient encore des joueurs hérités sans compte lié. Pour respecter la nouvelle logique, supprime-les puis ajoute les vrais membres depuis la liste de recherche.
                  </div>
                ) : null}

                {joueurs.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {joueurs.map((joueur) => (
                      <div key={joueur.id} className="flex items-center gap-3 p-2.5 bg-gray-50 rounded-xl">
                        <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-orange-100 text-[#E8521A] text-xs font-bold flex-shrink-0">
                          {joueur.numero}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-[#1A1A1A]">{joueur.nom}</div>
                          {joueur.membreNom ? (
                            <div className="text-xs text-[#1A8C7A] truncate">
                              Compte lié: {joueur.membreNom}
                              {joueur.membreEmail ? ` · ${joueur.membreEmail}` : ""}
                            </div>
                          ) : null}
                        </div>
                        <span className="text-xs text-gray-500 bg-white border border-gray-100 px-2 py-0.5 rounded-full">{joueur.poste}</span>
                        <button
                          type="button"
                          onClick={() => removeJoueur(joueur.id)}
                          className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-400">
                    Ajoute les joueurs maintenant ou plus tard, l&apos;API accepte une équipe sans effectif initial.
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowEquipeForm(false)} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                Annuler
              </button>
              <button onClick={() => void saveEquipe()} className="flex-1 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors">
                {editEquipe ? "Mettre à jour" : "Créer l'équipe"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRencontreForm ? (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowRencontreForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-[#1A1A1A] text-lg">{editRencontre ? "Modifier la rencontre" : "Nouvelle rencontre"}</h3>
              <button onClick={() => setShowRencontreForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-[#9A3E18]">
                Les rencontres sont créées à partir des vraies équipes enregistrées dans l&apos;admin. Deux équipes du même sport sont nécessaires.
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Équipe Domicile *</label>
                  <select
                    value={rencontreForm.equipeD}
                    onChange={(event) => {
                      const nextHomeTeam = equipes.find((team) => team.nom === event.target.value) ?? null;
                      setRencontreForm((current) => ({
                        ...current,
                        equipeD: event.target.value,
                        equipeE:
                          current.equipeE &&
                          nextHomeTeam &&
                          equipes.some(
                            (team) =>
                              team.nom === current.equipeE &&
                              team.id !== nextHomeTeam.id &&
                              team.sport === nextHomeTeam.sport
                          )
                            ? current.equipeE
                            : "",
                        sport: nextHomeTeam?.sport ?? ""
                      }));
                    }}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                  >
                    <option value="">Choisir l&apos;équipe domicile</option>
                    {homeTeamOptions.map((team) => (
                      <option key={team.id} value={team.nom}>
                        {team.nom} • {team.sport}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Équipe Extérieure *</label>
                  <select
                    value={rencontreForm.equipeE}
                    onChange={(event) => {
                      const nextAwayTeam = equipes.find((team) => team.nom === event.target.value) ?? null;
                      setRencontreForm((current) => ({
                        ...current,
                        equipeE: event.target.value,
                        sport: selectedHomeTeam?.sport ?? nextAwayTeam?.sport ?? current.sport
                      }));
                    }}
                    disabled={!selectedHomeTeam}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-400"
                  >
                    <option value="">
                      {selectedHomeTeam ? "Choisir l'équipe extérieure" : "Choisis d'abord l'équipe domicile"}
                    </option>
                    {awayTeamOptions.map((team) => (
                      <option key={team.id} value={team.nom}>
                        {team.nom}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Sport</label>
                  <input
                    type="text"
                    value={derivedMatchSport}
                    readOnly
                    placeholder="Sélectionne les équipes"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm bg-gray-50 text-gray-600 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Type</label>
                  <select
                    value={rencontreForm.type}
                    onChange={(event) => setRencontreForm({ ...rencontreForm, type: event.target.value as AdminMatch["type"] })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                  >
                    <option value="amical">Amical</option>
                    <option value="championnat">Championnat</option>
                    <option value="coupe">Coupe</option>
                    <option value="tournoi">Tournoi</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Date *</label>
                  <input
                    type="date"
                    value={rencontreForm.date}
                    onChange={(event) => setRencontreForm({ ...rencontreForm, date: event.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Heure *</label>
                  <input
                    type="time"
                    value={rencontreForm.heure}
                    onChange={(event) => setRencontreForm({ ...rencontreForm, heure: event.target.value })}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Lieu *</label>
                <input
                  type="text"
                  value={rencontreForm.lieu}
                  onChange={(event) => setRencontreForm({ ...rencontreForm, lieu: event.target.value })}
                  placeholder="Stade, salle, terrain..."
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Statut</label>
                <select
                  value={rencontreForm.statut}
                  onChange={(event) => setRencontreForm({ ...rencontreForm, statut: event.target.value as AdminMatch["statut"] })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                >
                  <option value="programme">Programmé</option>
                  <option value="en_cours">En cours</option>
                  <option value="termine">Terminé</option>
                  <option value="annule">Annulé</option>
                </select>
                <p className="mt-1 text-xs text-gray-400">
                  Pour faire remonter un résultat dans l&apos;interface publique, passe la rencontre à <span className="font-semibold">Terminé</span> et renseigne les deux scores.
                </p>
              </div>

              {(rencontreForm.statut === "termine" || rencontreForm.statut === "en_cours") ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Score</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      value={rencontreForm.scoreD}
                      onChange={(event) => setRencontreForm({ ...rencontreForm, scoreD: event.target.value })}
                      placeholder="0"
                      min="0"
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm text-center font-bold focus:outline-none focus:border-[#E8521A] transition-colors"
                    />
                    <span className="text-gray-400 font-bold text-lg">—</span>
                    <input
                      type="number"
                      value={rencontreForm.scoreE}
                      onChange={(event) => setRencontreForm({ ...rencontreForm, scoreE: event.target.value })}
                      placeholder="0"
                      min="0"
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-xl text-sm text-center font-bold focus:outline-none focus:border-[#E8521A] transition-colors"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1 text-center">{rencontreForm.equipeD || "Domicile"} — {rencontreForm.equipeE || "Extérieur"}</p>
                </div>
              ) : null}
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowRencontreForm(false)} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                Annuler
              </button>
              <button onClick={() => void saveRencontre()} className="flex-1 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors">
                {editRencontre ? "Mettre à jour" : "Créer la rencontre"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showTrainingForm ? (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowTrainingForm(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
            <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <h3 className="font-bold text-[#1A1A1A] text-lg">{editTraining ? "Modifier l'entraînement" : "Nouvel entraînement"}</h3>
              <button onClick={() => setShowTrainingForm(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="rounded-2xl border border-teal-100 bg-teal-50 px-4 py-3 text-sm text-[#11685c]">
                Chaque entraînement est obligatoirement rattaché à une vraie équipe créée dans l&apos;administration. C&apos;est ce planning qui remonte ensuite dans la page publique du club.
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Équipe *</label>
                <select
                  value={trainingForm.equipeId}
                  onChange={(event) => setTrainingForm((current) => ({ ...current, equipeId: event.target.value }))}
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                >
                  <option value="">Choisir l&apos;équipe</option>
                  {equipes.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.nom} • {team.sport}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Jour *</label>
                  <select
                    value={trainingForm.jour}
                    onChange={(event) => setTrainingForm((current) => ({ ...current, jour: event.target.value }))}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                  >
                    {TRAINING_DAYS.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Heure *</label>
                  <input
                    type="text"
                    value={trainingForm.heure}
                    onChange={(event) => setTrainingForm((current) => ({ ...current, heure: event.target.value }))}
                    placeholder="Ex: 17h00 - 19h00"
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Lieu *</label>
                <input
                  type="text"
                  value={trainingForm.lieu}
                  onChange={(event) => setTrainingForm((current) => ({ ...current, lieu: event.target.value }))}
                  placeholder="Ex: Stade Léopold Sédar Senghor"
                  className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-100 flex gap-3 sticky bottom-0 bg-white">
              <button onClick={() => setShowTrainingForm(false)} className="flex-1 py-3 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors">
                Annuler
              </button>
              <button onClick={() => void saveTraining()} className="flex-1 py-3 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors">
                {editTraining ? "Mettre à jour" : "Créer l'entraînement"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {selectedEquipe ? (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedEquipe(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[80vh] overflow-y-auto" onClick={(event) => event.stopPropagation()}>
            <div className="h-3 rounded-t-2xl" style={{ backgroundColor: selectedEquipe.couleur }}></div>
            <div className="p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${selectedEquipe.couleur}20` }}>
                    <i className="ri-shield-star-fill text-2xl" style={{ color: selectedEquipe.couleur }}></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A1A1A] text-lg">{selectedEquipe.nom}</h3>
                    <span className="text-sm text-gray-500">{selectedEquipe.sport}</span>
                  </div>
                </div>
                <button onClick={() => setSelectedEquipe(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
                  <i className="ri-close-line text-gray-500"></i>
                </button>
              </div>

              <div className="space-y-2 mb-5 p-4 bg-gray-50 rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Entraîneur</span>
                  <span className="font-medium text-[#1A1A1A]">{selectedEquipe.entraineur || "—"}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Statut</span>
                  <span className={`font-medium ${selectedEquipe.statut === "actif" ? "text-emerald-600" : "text-gray-500"}`}>
                    {selectedEquipe.statut === "actif" ? "Actif" : "Inactif"}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Effectif</span>
                  <span className="font-medium text-[#1A1A1A]">{selectedEquipe.joueurs.length} joueur{selectedEquipe.joueurs.length > 1 ? "s" : ""}</span>
                </div>
              </div>

              <h4 className="text-sm font-semibold text-gray-700 mb-3">Effectif complet</h4>
              {selectedEquipe.joueurs.length > 0 ? (
                <div className="space-y-2">
                  {selectedEquipe.joueurs.map((joueur) => (
                    <div key={joueur.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg text-white text-xs font-bold flex-shrink-0" style={{ backgroundColor: selectedEquipe.couleur }}>
                        {joueur.numero}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-[#1A1A1A]">{joueur.nom}</div>
                        {joueur.membreNom ? (
                          <div className="text-xs text-[#1A8C7A] truncate">
                            Compte lié: {joueur.membreNom}
                            {joueur.membreEmail ? ` · ${joueur.membreEmail}` : ""}
                          </div>
                        ) : null}
                      </div>
                      <span className="text-xs text-gray-500 bg-white border border-gray-100 px-2 py-0.5 rounded-full">{joueur.poste}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">Aucun joueur enregistré</p>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
